import { Directive, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';

/**
 * Same keystroke rule as TwoDigitDecimalNumberDirective (non-negative, up to two
 * decimal places) but tolerant of thousands separators already present in the field.
 * Money inputs are formatted to "1,234.56" on blur; testing the raw value against
 * the digits-only regex would reject every subsequent keystroke and freeze the input.
 */
@Directive({
    selector: '[moneyDecimalNumber]',
    standalone: true
})
export class MoneyDecimalNumberDirective implements AfterViewInit, OnDestroy {
    // Allow non-negative decimal numbers with up to two decimal places
    private regex: RegExp = new RegExp(/^\d*\.?\d{0,2}$/g);
    // Allow key codes for special events.
    private specialKeys: Array<string> = ['Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Del', 'Delete'];

    private inputEl: HTMLInputElement | null = null;
    private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

    constructor(private el: ElementRef) {
    }

    ngAfterViewInit(): void {
        const native = this.el.nativeElement as HTMLElement;
        if (native && (native.tagName === 'INPUT' || (native as any).value !== undefined)) {
            this.inputEl = native as unknown as HTMLInputElement;
        } else if (native) {
            this.inputEl = native.querySelector && (native.querySelector('input') as HTMLInputElement | null);
        }

        if (this.inputEl) {
            this.keydownHandler = (event: KeyboardEvent) => this.handleKeyDown(event);
            this.inputEl.addEventListener('keydown', this.keydownHandler as EventListener);
        }
    }

    private handleKeyDown(event: KeyboardEvent) {
        if (this.specialKeys.indexOf(event.key) !== -1) {
            return;
        }
        const el = this.inputEl as HTMLInputElement;
        const current: string = el.value || '';
        const position = (el.selectionStart as number) || 0;
        const key = event.key === 'Decimal' ? '.' : event.key;
        // Build the candidate from the raw value so selectionStart stays meaningful,
        // then strip separators before testing so existing commas don't fail the regex.
        const next: string = [current.slice(0, position), key, current.slice(position)].join('');
        if (next && !String(next).replace(/,/g, '').match(this.regex)) {
            event.preventDefault();
        }
    }

    ngOnDestroy(): void {
        if (this.inputEl && this.keydownHandler) {
            this.inputEl.removeEventListener('keydown', this.keydownHandler as EventListener);
        }
    }
}
